/**
 * Workflows (CRM phase 8; "Automations" in the app), as TanStack Query
 * hooks: the list and its templates, one workflow, its runs and a run's
 * step log, its analytics and email figures, the dry run, and every write.
 *
 * A buyer, supplier, pending or otherwise scoped login is refused by the
 * server everywhere here, so the page is not on their menu at all. Anyone
 * else reads; `can_manage` (manage_workflows) decides who may change one.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

type S = components['schemas'];
export type Workflow = S['Workflow'];
export type WorkflowSummary = S['WorkflowSummary'];
export type WorkflowListResponse = S['WorkflowListResponse'];
export type WorkflowTemplate = S['WorkflowTemplate'];
export type WorkflowTrigger = S['WorkflowTrigger'];
export type WorkflowStep = S['WorkflowStep'];
export type TriggerKind = S['TriggerKind'];
export type StepKind = S['StepKind'];
export type RecordType = S['RecordType'];
export type EmailEventKind = S['EmailEventKind'];
export type WorkflowRun = S['WorkflowRun'];
export type WorkflowRunStatus = S['WorkflowRunStatus'];
export type WorkflowRunDetail = S['WorkflowRunDetail'];
export type WorkflowRunStep = S['WorkflowRunStep'];
export type WorkflowRunListResponse = S['WorkflowRunListResponse'];
export type WorkflowTestResult = S['WorkflowTestResult'];
export type TestWorkflowBody = S['TestWorkflowBody'];
export type CreateWorkflowBody = S['CreateWorkflowBody'];
export type UpdateWorkflowBody = S['UpdateWorkflowBody'];
export type WorkflowGoal = S['WorkflowGoal'];
export type GoalOutcome = S['GoalOutcome'];
export type WorkflowTestStep = S['WorkflowTestStep'];
export type WorkflowAnalytics = S['WorkflowAnalytics'];
export type WorkflowStepReach = S['WorkflowStepReach'];
export type EnrolBody = S['EnrolBody'];
export type EnrolPreview = S['EnrolPreview'];
export type EnrolResponse = S['EnrolResponse'];
export type EnrolSegment = S['EnrolSegment'];
export type EnrolSegmentKind = S['EnrolSegmentKind'];
export type EnrolScheduleInput = S['EnrolScheduleInput'];
export type WorkflowEnrolment = S['WorkflowEnrolment'];
export type WorkflowEnrolSchedule = S['WorkflowEnrolSchedule'];
export type WorkflowEnrolmentsResponse = S['WorkflowEnrolmentsResponse'];
export type EmailTemplateLink = S['EmailTemplateLink'];
export type WorkflowHealth = S['WorkflowHealth'];
export type WorkflowHealthState = S['WorkflowHealthState'];
export type WorkflowOverlap = S['WorkflowOverlap'];
export type WorkflowFolder = S['WorkflowFolder'];
export type WorkflowFoldersResponse = S['WorkflowFoldersResponse'];
export type OrganiseWorkflowBody = S['OrganiseWorkflowBody'];
export type WorkflowCheckBody = S['WorkflowCheckBody'];
export type WorkflowCheckResponse = S['WorkflowCheckResponse'];
export type WorkflowMatch = S['WorkflowMatch'];
export type WorkflowRefType = S['WorkflowRefType'];
export type WorkflowUsedByResponse = S['WorkflowUsedByResponse'];
export type WorkflowSendBudget = S['WorkflowSendBudget'];
export type WorkflowSendLimit = S['WorkflowSendLimit'];
export type UsedByWorkflow = S['UsedByWorkflow'];
export type WorkflowEmailStats = S['WorkflowEmailStats'];
export type WorkflowEmailStepStats = S['WorkflowEmailStepStats'];
export type WorkflowEmailLink = S['WorkflowEmailLink'];

const LIST = ['workflows'];

/** Every workflow, and what the builder offers. */
export function useWorkflows(enabled = true) {
  return useQuery({
    queryKey: LIST,
    queryFn: async () => unwrap(await api.GET('/api/v1/workflows', {}), 'WorkflowListResponse'),
    staleTime: 30 * 1000,
    enabled,
  });
}

export function useWorkflow(id: string | null) {
  return useQuery({
    queryKey: ['workflow', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/workflows/{id}', { params: { path: { id: id! } } }), 'Workflow'),
    enabled: !!id,
  });
}

export function useWorkflowRuns(id: string | null, status: WorkflowRunStatus | null, page: number, pageSize = 25) {
  return useQuery({
    queryKey: ['workflow-runs', id, status, page, pageSize],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/workflows/{id}/runs', {
          params: {
            path: { id: id! },
            query: { page, page_size: pageSize, ...(status ? { status } : {}) },
          },
        }),
        'WorkflowRunListResponse',
      ),
    enabled: !!id,
    placeholderData: keepPreviousData,
    staleTime: 15 * 1000,
  });
}

export function useWorkflowRun(id: string | null, runId: string | null) {
  return useQuery({
    queryKey: ['workflow-run', id, runId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/workflows/{id}/runs/{run_id}', { params: { path: { id: id!, run_id: runId! } } }),
        'WorkflowRunDetail',
      ),
    enabled: !!id && !!runId,
  });
}

/** Every write, each refreshing the list and the workflow it touched. */
export function useWorkflowEdits() {
  const qc = useQueryClient();
  const done = (w?: Workflow) => {
    qc.invalidateQueries({ queryKey: LIST });
    if (w) qc.setQueryData(['workflow', w.id], w);
  };
  const create = useMutation({
    mutationFn: async (body: CreateWorkflowBody) =>
      unwrap(await api.POST('/api/v1/workflows', { body }), 'Workflow'),
    onSuccess: done,
  });
  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateWorkflowBody }) =>
      unwrap(await api.PATCH('/api/v1/workflows/{id}', { params: { path: { id } }, body }), 'Workflow'),
    onSuccess: done,
  });
  const setEnabled = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const params = { params: { path: { id } } };
      return enabled
        ? unwrap(await api.POST('/api/v1/workflows/{id}/enable', params), 'Workflow')
        : unwrap(await api.POST('/api/v1/workflows/{id}/disable', params), 'Workflow');
    },
    onSuccess: done,
  });
  const duplicate = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.POST('/api/v1/workflows/{id}/duplicate', { params: { path: { id } } }), 'Workflow'),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/workflows/{id}', { params: { path: { id } } });
      if (res.error !== undefined) {
        const e = res.error as { detail?: string };
        throw new Error(e.detail ?? 'Delete failed');
      }
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: LIST });
      qc.invalidateQueries({ queryKey: USED_BY });
      qc.removeQueries({ queryKey: ['workflow', id] });
    },
  });
  const archive = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.POST('/api/v1/workflows/{id}/archive', { params: { path: { id } } }), 'Workflow'),
    onSuccess: (w) => {
      done(w);
      qc.invalidateQueries({ queryKey: USED_BY });
    },
  });
  const restore = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.POST('/api/v1/workflows/{id}/restore', { params: { path: { id } } }), 'Workflow'),
    onSuccess: (w) => {
      done(w);
      qc.invalidateQueries({ queryKey: USED_BY });
    },
  });
  const organise = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: OrganiseWorkflowBody }) =>
      unwrap(await api.PATCH('/api/v1/workflows/{id}/organise', { params: { path: { id } }, body }), 'Workflow'),
    onSuccess: done,
  });
  return { create, update, setEnabled, duplicate, remove, archive, restore, organise };
}

/** Make, rename and delete folders. Deleting one moves its workflows to "No folder". */
export function useWorkflowFolderEdits() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: LIST });
  const create = useMutation({
    mutationFn: async (name: string) =>
      unwrap(await api.POST('/api/v1/workflow-folders', { body: { name } }), 'WorkflowFolder'),
    onSuccess: refresh,
  });
  const rename = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      unwrap(await api.PATCH('/api/v1/workflow-folders/{id}', { params: { path: { id } }, body: { name } }), 'WorkflowFolder'),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/workflow-folders/{id}', { params: { path: { id } } });
      if (res.error !== undefined) {
        const e = res.error as { detail?: string };
        throw new Error(e.detail ?? 'Not deleted');
      }
      return id;
    },
    onSuccess: refresh,
  });
  return { create, rename, remove };
}

/**
 * Before a save: switched-on workflows this one looks like, and those it
 * could email someone alongside on the same day. Changes nothing.
 */
export function useCheckWorkflow() {
  return useMutation({
    mutationFn: async (body: WorkflowCheckBody) =>
      unwrap(await api.POST('/api/v1/workflows/check', { body }), 'WorkflowCheckResponse'),
  });
}

const USED_BY = ['workflows-used-by'];

/** "Used by" once, for a confirm before an action. Null when this login may not read workflows. */
export async function fetchUsedBy(refType: WorkflowRefType, refId: string): Promise<WorkflowUsedByResponse | null> {
  const res = await api.GET('/api/v1/workflows/used-by', { params: { query: { ref_type: refType, ref_id: refId } } });
  if (res.response.status === 403) return null;
  return unwrap(res, 'WorkflowUsedByResponse');
}

/**
 * The workflows that refer to something, for the
 * read-only "Used by" panels around the app. Null for a login that may not
 * read workflows (the server says 403), so the panel simply does not show.
 */
export function useWorkflowsUsedBy(refType: WorkflowRefType, refId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: [...USED_BY, refType, refId],
    queryFn: async () => {
      const res = await api.GET('/api/v1/workflows/used-by', {
        params: { query: { ref_type: refType, ref_id: refId! } },
      });
      if (res.response.status === 403) return null;
      return unwrap(res, 'WorkflowUsedByResponse');
    },
    enabled: enabled && !!refId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

/**
 * A template's links and their names (the SES link tag the email editor
 * writes), for the "link clicked" trigger and waits.
 */
export function useTemplateLinks(templateId: string | null | undefined) {
  return useQuery({
    queryKey: ['template-links', templateId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/email-templates/{id}/links', { params: { path: { id: templateId! } } }),
        'EmailTemplateLinksResponse',
      ),
    enabled: !!templateId,
    staleTime: 30 * 1000,
  });
}

/**
 * What a send step's emails count towards today: its brand's daily limit
 * (shared with the brand's campaigns, warm-up included) and the workflow's
 * own. With no brand, the template's From domain picks the brand.
 */
export function useWorkflowSendBudget(brandId: string | null, templateId: string | null, workflowId: string | null) {
  return useQuery({
    queryKey: ['workflow-send-budget', brandId, templateId, workflowId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/workflows/send-budget', {
          params: {
            query: {
              brand_id: brandId || undefined,
              template_id: templateId || undefined,
              workflow_id: workflowId || undefined,
            },
          },
        }),
        'WorkflowSendBudget',
      ),
    enabled: !!brandId || !!templateId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

/** Runs by outcome, goal conversion and how many reached each step. */
export function useWorkflowAnalytics(id: string | null) {
  return useQuery({
    queryKey: ['workflow-analytics', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/workflows/{id}/analytics', { params: { path: { id: id! } } }), 'WorkflowAnalytics'),
    enabled: !!id,
    staleTime: 15 * 1000,
  });
}

/**
 * How a workflow's emails did over the last `days` (London, today
 * included): totals, per send step, per day and per link. One converted
 * from an email automation includes that automation's history.
 */
export function useWorkflowEmailStats(id: string | null, days: number) {
  return useQuery({
    queryKey: ['workflow-email-stats', id, days],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/workflows/{id}/email-stats', { params: { path: { id: id! }, query: { days } } }),
        'WorkflowEmailStats',
      ),
    enabled: !!id,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

/** A workflow's enrolments (newest first) and its schedules. */
export function useEnrolments(id: string | null) {
  return useQuery({
    queryKey: ['workflow-enrolments', id],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/workflows/{id}/enrolments', { params: { path: { id: id! } } }),
        'WorkflowEnrolmentsResponse',
      ),
    enabled: !!id,
    staleTime: 10 * 1000,
  });
}

/**
 * How many a segment would enrol now. A POST that changes nothing, so it is
 * a query: it refreshes as the choices change.
 */
export function useEnrolPreview(id: string | null, body: EnrolBody | null) {
  return useQuery({
    queryKey: ['enrol-preview', id, body],
    queryFn: async () =>
      unwrap(
        await api.POST('/api/v1/workflows/{id}/enrol/preview', { params: { path: { id: id! } }, body: body! }),
        'EnrolPreview',
      ),
    enabled: !!id && !!body,
    staleTime: 15 * 1000,
    retry: false,
  });
}

/** Enrol now (a queued job) or set a schedule; and stop a schedule. */
export function useEnrolEdits() {
  const qc = useQueryClient();
  const refresh = (id: string) => {
    qc.invalidateQueries({ queryKey: LIST });
    qc.invalidateQueries({ queryKey: ['workflow-enrolments', id] });
    qc.invalidateQueries({ queryKey: ['workflow-analytics', id] });
    qc.invalidateQueries({ queryKey: ['workflow-runs', id] });
  };
  const enrol = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: EnrolBody }) =>
      unwrap(await api.POST('/api/v1/workflows/{id}/enrol', { params: { path: { id } }, body }), 'EnrolResponse'),
    onSuccess: (_r, { id }) => refresh(id),
  });
  const stopSchedule = useMutation({
    mutationFn: async ({ id, scheduleId }: { id: string; scheduleId: string }) => {
      const res = await api.DELETE('/api/v1/workflows/{id}/enrol-schedules/{schedule_id}', {
        params: { path: { id, schedule_id: scheduleId } },
      });
      if (res.error !== undefined) {
        const e = res.error as { detail?: string };
        throw new Error(e.detail ?? 'Not stopped');
      }
    },
    onSuccess: (_r, { id }) => refresh(id),
  });
  /** Cancel a schedule: a "send once" before it fires, or while it sends (sent emails stay sent). */
  const cancelSchedule = useMutation({
    mutationFn: async ({ id, scheduleId }: { id: string; scheduleId: string }) =>
      unwrap(
        await api.POST('/api/v1/workflows/{id}/enrol-schedules/{schedule_id}/cancel', {
          params: { path: { id, schedule_id: scheduleId } },
        }),
        'WorkflowEnrolSchedule',
      ),
    onSuccess: (_r, { id }) => {
      refresh(id);
    },
  });
  return { enrol, stopSchedule, cancelSchedule };
}

/** A dry run on one record. Read only on the server: nothing is changed. */
export function useTestWorkflow() {
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: TestWorkflowBody }) =>
      unwrap(await api.POST('/api/v1/workflows/{id}/test', { params: { path: { id } }, body }), 'WorkflowTestResult'),
  });
}
